// Preview generation utilities for import

export interface PreviewRecord {
  rowIndex: number;
  entityType: "CONTACT" | "COMPANY" | "SALE";
  data: Record<string, any>;
  status: "new" | "update" | "duplicate";
  duplicateWith?: number[];
  validationErrors?: Array<{ field: string; message: string }>;
}

/**
 * Generate preview records from CSV rows and mappings
 */
export const generatePreviewRecords = (
  csvRows: Record<string, string>[],
  mappings: Record<string, any[]>
): { contacts: PreviewRecord[]; companies: PreviewRecord[]; deals: PreviewRecord[] } => {
  const contacts: PreviewRecord[] = [];
  const companies: PreviewRecord[] = [];
  const deals: PreviewRecord[] = [];

  const contactMappings = mappings["CONTACT"] || [];
  const companyMappings = mappings["COMPANY"] || [];
  const saleMappings = mappings["SALE"] || [];

  csvRows.forEach((row, rowIndex) => {
    // Generate contact preview
    if (contactMappings.length > 0) {
      const contactData: Record<string, any> = {};
      let hasContactData = false;

      contactMappings.forEach((mapping: any) => {
        const sourceField = mapping?.source?.value;
        const destinationField = mapping?.destination?.value;

        if (sourceField && destinationField && row[sourceField]) {
          contactData[destinationField] = row[sourceField];
          hasContactData = true;
        }
      });

      // Only create preview if we have name or email
      if (hasContactData && (contactData.name || contactData.email)) {
        contacts.push({
          rowIndex,
          entityType: "CONTACT",
          data: contactData,
          status: "new", // Will be updated by markDuplicatesInPreview
        });
      }
    }

    // Generate company preview
    if (companyMappings.length > 0) {
      const companyData: Record<string, any> = {};
      let hasCompanyData = false;

      companyMappings.forEach((mapping: any) => {
        const sourceField = mapping?.source?.value;
        const destinationField = mapping?.destination?.value;

        if (sourceField && destinationField && row[sourceField]) {
          companyData[destinationField] = row[sourceField];
          hasCompanyData = true;
        }
      });

      // Only create preview if we have name
      if (hasCompanyData && companyData.name) {
        companies.push({
          rowIndex,
          entityType: "COMPANY",
          data: companyData,
          status: "new", // Will be updated by markDuplicatesInPreview
        });
      }
    }

    // Generate deal preview
    if (saleMappings.length > 0) {
      const dealData: Record<string, any> = {};
      let hasDealData = false;

      saleMappings.forEach((mapping: any) => {
        const sourceField = mapping?.source?.value;
        const destinationField = mapping?.destination?.value;

        if (sourceField && destinationField && row[sourceField]) {
          dealData[destinationField] = row[sourceField];
          hasDealData = true;
        }
      });

      // Only create preview if we have name (deal title)
      if (hasDealData && dealData.name) {
        deals.push({
          rowIndex,
          entityType: "SALE",
          data: dealData,
          status: "new", // Will be updated by markDuplicatesInPreview
        });
      }
    }
  });

  return { contacts, companies, deals };
};

/**
 * Mark duplicates in preview records
 * This is a simplified version - in production, should check against existing records from backend
 */
export const markDuplicatesInPreview = (records: PreviewRecord[]): PreviewRecord[] => {
  // For now, detect duplicates within the preview itself
  // In production, this should check against existing records from backend API

  const emailMap = new Map<string, number[]>();
  const domainMap = new Map<string, number[]>();
  const dealNameMap = new Map<string, number[]>();

  records.forEach((record, index) => {
    if (record.entityType === "CONTACT" && record.data.email) {
      const email = record.data.email.toLowerCase().trim();
      if (!emailMap.has(email)) {
        emailMap.set(email, []);
      }
      emailMap.get(email)!.push(index);
    }

    if (record.entityType === "COMPANY") {
      const domain =
        record.data.domain?.toLowerCase().trim() || record.data.name?.toLowerCase().trim();
      if (domain) {
        if (!domainMap.has(domain)) {
          domainMap.set(domain, []);
        }
        domainMap.get(domain)!.push(index);
      }
    }

    if (record.entityType === "SALE" && record.data.name) {
      const name = String(record.data.name).toLowerCase().trim();
      if (!dealNameMap.has(name)) {
        dealNameMap.set(name, []);
      }
      dealNameMap.get(name)!.push(index);
    }
  });

  // Mark duplicates
  return records.map((record, index) => {
    let duplicateIndices: number[] = [];

    if (record.entityType === "CONTACT" && record.data.email) {
      const email = record.data.email.toLowerCase().trim();
      const indices = emailMap.get(email) || [];
      if (indices.length > 1) {
        duplicateIndices = indices.filter((i) => i !== index);
      }
    }

    if (record.entityType === "COMPANY") {
      const domain =
        record.data.domain?.toLowerCase().trim() || record.data.name?.toLowerCase().trim();
      if (domain) {
        const indices = domainMap.get(domain) || [];
        if (indices.length > 1) {
          duplicateIndices = indices.filter((i) => i !== index);
        }
      }
    }

    if (record.entityType === "SALE" && record.data.name) {
      const name = String(record.data.name).toLowerCase().trim();
      const indices = dealNameMap.get(name) || [];
      if (indices.length > 1) {
        duplicateIndices = indices.filter((i) => i !== index);
      }
    }

    if (duplicateIndices.length > 0) {
      return {
        ...record,
        status: "duplicate" as const,
        duplicateWith: duplicateIndices,
      };
    }

    return record;
  });
};
